import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { useState, type ReactNode } from 'react'

export function readSectionOpenFromStorage(key: string): boolean {
    try {
        const raw = window.localStorage.getItem(key)
        if (raw === '1' || raw === 'true') return true
        return false
    } catch {
        return false
    }
}

export function persistSectionOpen(key: string, open: boolean) {
    try {
        window.localStorage.setItem(key, open ? '1' : '0')
    } catch {
        // ignore storage failures
    }
}

export function SectionCard({
    title,
    children,
    subtitle,
    headerRight,
    titleAccessory,
    fillBody = false,
    subtitleVariant = 'compact',
}: {
    title?: string
    children: ReactNode
    subtitle?: ReactNode
    headerRight?: ReactNode
    titleAccessory?: ReactNode
    fillBody?: boolean
    subtitleVariant?: 'compact' | 'relaxed'
}) {
    const hasHeader = title || subtitle || headerRight || titleAccessory
    const subtitleClassName =
        subtitleVariant === 'relaxed'
            ? 'mt-1 text-xs leading-relaxed text-gray-500'
            : 'mt-1 text-[11px] text-gray-400'

    return (
        <section
            className={`rounded-xl border border-gray-200 bg-white p-5 shadow-sm ${
                fillBody ? 'flex h-full flex-col' : ''
            }`}
            {...(title ? { 'data-bk-container': title } : {})}
        >
            {hasHeader ? (
                <div className="mb-4 flex shrink-0 flex-wrap items-start justify-between gap-3">
                    <div>
                        <div className="flex items-center gap-1.5">
                            {title ? <h2 className="container-title">{title}</h2> : null}
                            {titleAccessory}
                        </div>
                        {subtitle ? (
                            subtitleVariant === 'relaxed' ? (
                                <div className={subtitleClassName}>{subtitle}</div>
                            ) : (
                                <p className={subtitleClassName}>{subtitle}</p>
                            )
                        ) : null}
                    </div>
                    {headerRight}
                </div>
            ) : null}
            <div className={fillBody ? 'flex min-h-[400px] flex-1 flex-col' : undefined}>
                {children}
            </div>
        </section>
    )
}

export function CollapsibleSectionCard({
    storageKey,
    title,
    children,
    subtitle,
    headerRight,
    open: controlledOpen,
    onOpenChange,
}: {
    storageKey: string
    title: string
    children: ReactNode
    subtitle?: string
    headerRight?: ReactNode
    open?: boolean
    onOpenChange?: (open: boolean) => void
}) {
    const [internalOpen, setInternalOpen] = useState(() => readSectionOpenFromStorage(storageKey))
    const isControlled = onOpenChange !== undefined
    const open = isControlled ? (controlledOpen ?? false) : internalOpen

    function handleToggle() {
        const next = !open
        if (isControlled) {
            onOpenChange(next)
        } else {
            setInternalOpen(next)
            persistSectionOpen(storageKey, next)
        }
    }

    return (
        <section
            className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm"
            data-bk-container={title}
        >
            <div className="flex flex-wrap items-start justify-between gap-3">
                <button
                    type="button"
                    className="flex min-w-0 flex-1 items-start gap-2 text-left"
                    onClick={handleToggle}
                    aria-expanded={open}
                >
                    <ChevronDownIcon
                        className={`mt-0.5 h-4 w-4 shrink-0 text-gray-500 transition-transform ${open ? '' : '-rotate-90'}`}
                        aria-hidden="true"
                    />
                    <div className="min-w-0">
                        <h2 className="container-title">{title}</h2>
                        {subtitle ? <p className="mt-1 text-[11px] text-gray-400">{subtitle}</p> : null}
                    </div>
                </button>
                {open && headerRight ? <div className="shrink-0">{headerRight}</div> : null}
            </div>
            {open ? <div className="mt-4">{children}</div> : null}
        </section>
    )
}
